import { useId, useRef, useState } from 'react';
import { Alert, Button, Modal } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import { isApiError } from '@/services/api';
import { useActivateAgreementMutation } from '../hooks/useActivateAgreementMutation';
import type { AgreementStatus } from '../types/agreements.types';

interface Props {
    agreementId: string;
    status: AgreementStatus | string;
}

const noticeHeadingId = 'agreement-draft-notice-heading';

/** D3 — the Activate action. Draft-only, gated on `agreement.activate`, no
 *  optimistic update, 422 surfaced verbatim. Open focus and close restore are
 *  handled by the shared `Modal` (via `initialFocusRef`); this component no
 *  longer needs its own focus effect. */
export function AgreementDraftNotice({ agreementId, status }: Props) {
    const [isOpen, setIsOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const mutation = useActivateAgreementMutation(agreementId);
    const modalHeadingId = useId();

    const confirmWrapperRef = useRef<HTMLSpanElement>(null);

    if (status !== 'draft') {
        return null;
    }

    const handleOpen = () => {
        setErrorMessage(null);
        setIsOpen(true);
    };

    const handleClose = () => {
        if (mutation.isPending) return;
        setIsOpen(false);
    };

    const handleConfirm = () => {
        setErrorMessage(null);
        mutation.mutate(undefined, {
            onSuccess: () => {
                setIsOpen(false);
            },
            onError: (error: unknown) => {
                // 422 surfaced VERBATIM: the shared Axios interceptor already lifts the
                // backend's `message` field (e.g. `Agreement ... is active: transition
                // "activate" is not permitted from that state.`) onto ApiError.message,
                // which already names the current state — no client-built copy, never a
                // generic "Something went wrong".
                setErrorMessage(
                    isApiError(error) ? error.message : 'Failed to activate this agreement. Please try again.',
                );
            },
        });
    };

    return (
        <section aria-labelledby={noticeHeadingId}>
            <Alert variant="warning" role="note">
                <div>
                    <h2 id={noticeHeadingId} className="font-semibold">
                        Draft agreement
                    </h2>
                    <p className="mt-1">
                        This agreement is awaiting activation. It will not become live until it is activated.
                    </p>
                    <PermissionGuard permission={{ resource: 'agreement', action: 'activate' }}>
                        <span className="mt-3 inline-block">
                            <Button type="button" size="sm" variant="warning" onClick={handleOpen}>
                                Activate
                            </Button>
                        </span>
                    </PermissionGuard>
                </div>
            </Alert>

            <Modal
                isOpen={isOpen}
                onClose={handleClose}
                ariaLabelledBy={modalHeadingId}
                className="max-w-md p-6"
                initialFocusRef={confirmWrapperRef}
            >
                <h2 id={modalHeadingId} className="mb-2 text-lg font-semibold">
                    Activate agreement
                </h2>
                <p className="mb-4 text-sm text-mp-muted">
                    This starts the agreement today. This action cannot be undone.
                </p>
                <div className="space-y-4">
                    {errorMessage && <Alert variant="error">{errorMessage}</Alert>}
                    <div className="flex justify-end gap-2">
                        <Button type="button" variant="clear" disabled={mutation.isPending} onClick={handleClose}>
                            Cancel
                        </Button>
                        <span ref={confirmWrapperRef}>
                            <Button type="button" variant="warning" disabled={mutation.isPending} onClick={handleConfirm}>
                                {mutation.isPending ? 'Activating…' : 'Confirm activation'}
                            </Button>
                        </span>
                    </div>
                </div>
            </Modal>
        </section>
    );
}
